import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Import assets
import { Images } from "../../../utils/Images";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const CMS_PAGE = "How To Donate";
const CMS_SECTION = "How To Donate";


// Styled components
const Container = styled(Box)(({ theme, isMobile, isTablet }) => ({
  background: "#FFFFFF",
  borderRadius: "12px",
  border: "1px solid #F1F1F1",
  padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
  width: isMobile ? "94.44%" : "100%",
  margin: "0 auto",
}));

const GreenBox = styled(Box)(({ theme, isMobile, isTablet }) => ({
  background: "#F5F8ED",
  borderRadius: "5px",
  padding: isMobile ? "8px 0px 8px 12px" : isTablet ? "9px 0px 9px 14px" : "10px 0px 10px 16px",
  marginBottom: isMobile ? "12px" : isTablet ? "13px" : "14px",
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
}));

const GreenText = styled(Typography)(({ theme, isMobile, isTablet }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "12px" : isTablet ? "13px" : "14px",
  lineHeight: isMobile ? "18px" : isTablet ? "19px" : "21px",
  letterSpacing: "0%",
  color: "#8EA946",
}));

const VideoContainer = styled(Box)(({ theme, isMobile, isTablet }) => ({
  height: isMobile ? "160px" : isTablet ? "115px" : "133px",
  borderRadius: "10px",
  background: "#F8F8F8",
  marginBottom: 0,
  position: "relative",
  overflow: "hidden",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.02)",
  },
  "@media (min-width: 1440px)": {
    height: "auto",
    minHeight: "133px",
  },
}));

const VideoThumbnail = styled(Box)(({ theme, isMobile }) => ({
  width: "100%",
  height: "100%",
  position: "relative",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "10px",
  },
  "@media (min-width: 1440px)": {
    "& img": {
      objectFit: "contain",
      height: "auto",
      maxHeight: "400px",
    },
  },
}));

const PlayButton = styled(Box)(({ theme, isMobile, isTablet }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: isMobile ? "36px" : isTablet ? "42px" : "48px",
  height: isMobile ? "36px" : isTablet ? "42px" : "48px",
//   background: "rgba(0, 0, 0, 0.7)",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    width: isMobile ? "22px" : isTablet ? "26px" : "30px",
    height: isMobile ? "22px" : isTablet ? "26px" : "30px",
  },
}));

const Subtitle = styled(Typography)(({ theme, isMobile, isTablet }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: isMobile ? "12px" : isTablet ? "13px" : "14px",
  lineHeight: isMobile ? "18px" : isTablet ? "20px" : "22px",
  letterSpacing: "0%",
  color: "#686866",
  marginBottom: isMobile ? "12px" : isTablet ? "13px" : "14px",
}));

// Custom Donate Button with 100% width - copied from DonateButtonWithArrow styles
const CustomDonateButton = styled(Button)(({ theme, isMobile, isTablet, isMedicalPage }) => ({
  backgroundColor: isMobile && isMedicalPage ? "#1D1D1B" : "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  // padding: isMobile ? "12px 16px" : isTablet ? "14px 20px" : "15px 24px",
  width: "100%", // 100% width for responsiveness
  height: isMobile ? "44px" : isTablet ? "48px" : "52px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 700,
  fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
  lineHeight: isMobile ? "13px" : isTablet ? "14px" : "15px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : isTablet ? "7px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: isMobile && isMedicalPage ? "#1D1D1B" : "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
}));

// Arrow Icon Component
const ArrowUpRightIcon = ({ isMobile, isTablet }) => (
  <img
    src={Images.arrowUpRight}
    alt="arrow up right"
    width={isMobile ? "16" : isTablet ? "18" : "20"}
    height={isMobile ? "16" : isTablet ? "18" : "20"}
    style={{ filter: "brightness(0) invert(1)" }} // This makes the icon white
  />
);

const HowToDonate = ({ isMedicalPage = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const howToDonateCms = useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    return (
      data.find(
        (s) => s?.page === CMS_PAGE && s?.section === CMS_SECTION
      ) || null
    );
  }, [cmsHome]);

  const sectionTitle = howToDonateCms?.section_title ?? "";
  const title = howToDonateCms?.title ?? "";
  const description = howToDonateCms?.description ?? "";
  const thumbnailImage = howToDonateCms?.image ?? "";
  const videoLink = howToDonateCms?.link ?? "";

  const handleVideoClick = () => {
    if (videoLink) window.open(videoLink, "_blank");
  };

  const handleDonateClick = () => {
    navigate("/monthly-donation");
  };

  return (
    <Container isMobile={isMobile} isTablet={isTablet}>
      {/* Green Box with section title from CMS */}
      {sectionTitle.trim() ? (
      <GreenBox isMobile={isMobile} isTablet={isTablet}>
        <GreenText isMobile={isMobile} isTablet={isTablet}>
          {sectionTitle.trim().endsWith(":")
            ? sectionTitle
            : `${sectionTitle}:`}
        </GreenText>
      </GreenBox>
      ) : null}

      {/* Video Section: thumbnail from CMS image, link from CMS link */}
      {thumbnailImage ? (
      <VideoContainer isMobile={isMobile} isTablet={isTablet} onClick={handleVideoClick}>
        <VideoThumbnail isMobile={isMobile}>
          <img src={thumbnailImage} alt="Video Thumbnail" />
          <PlayButton isMobile={isMobile} isTablet={isTablet}>
            <img src={Images.youtube} alt="Play" />
          </PlayButton>
        </VideoThumbnail>
      </VideoContainer>
      ) : null}

      <Divider
        sx={{
          width: "100%",
          borderColor: "#E0E0E0",
          opacity: 1,
          marginTop: isMobile ? "12px" : isTablet ? "13px" : "14px",
          marginBottom: isMobile ? "12px" : isTablet ? "13px" : "14px",
        }}
      />

      {/* Main title from CMS — same pale green bar + green text */}
      {title.trim() ? (
      <GreenBox isMobile={isMobile} isTablet={isTablet}>
        <GreenText isMobile={isMobile} isTablet={isTablet} component="h2">
          {title}
        </GreenText>
      </GreenBox>
      ) : null}

      {/* Description from CMS */}
      {description.trim() ? (
      <Subtitle isMobile={isMobile} isTablet={isTablet}>
        {description}
      </Subtitle>
      ) : null}

      {/* Custom Donate Button with 100% width */}
      <CustomDonateButton
        onClick={handleDonateClick}
        isMobile={isMobile}
        isTablet={isTablet}
        isMedicalPage={isMedicalPage}
      >
        Donate Monthly
        <ArrowUpRightIcon isMobile={isMobile} isTablet={isTablet} />
      </CustomDonateButton>
    </Container>
  );
};

export default HowToDonate;
